import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import { ColumnDef } from "@tanstack/react-table";
import { useContextSelector } from "use-context-selector";
import { useMediaQuery } from "usehooks-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  IPrinter,
  IStore,
  UpdateShopInputDTO,
} from "../../../../../@types/Stores";
import { PageHeader } from "../../../../../components/Headers/PageHeader";
import { Button } from "../../../../../components/New/Button";
import { DrawerComponent } from "../../../../../components/New/Drawer";
import { Input } from "../../../../../components/New/Input";
import { Modal } from "../../../../../components/New/Modal";
import { DeleteIcon } from "../../../../../components/New/Modal/components/DeleteIcon";
import { DeleteModal } from "../../../../../components/New/Modal/components/DeleteModal";
import { Table } from "../../../../../components/New/Table";
import { SwitchInput } from "../../../../../components/Switch/SwitchInput";
import { StoresContext } from "../../../../../contexts/StoresContext";
import { wrapperRequests } from "../../../../../services/api";
import { routesURL } from "../../../../../services/routesUrl";
import {
  hasUnsavedChanges,
  useLeavePageConfirm,
} from "../../../../../shared/HasUnsavedChanges";
import { ReduceString } from "../../../../../shared/ReduceString";
import { toastify } from "../../../../../shared/Toastify";
import { PrintersModalContent } from "../components/PrintersModalContent";
import { StoreSchema, TStoreSchema } from "./schema";
import * as S from "./styles";

function joinArray(input?: string[], separator = ", "): string {
  return Array.isArray(input) ? input.join(separator) : "";
}

const DefaultShopUsersResponse = z.object({
  users: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      email: z.string(),
    }),
  ),
});

type DefaultShopUser = z.infer<
  typeof DefaultShopUsersResponse
>["users"][number];

const defaultShopBlockMessage =
  "This store cannot be deactivated as it is set as the default store";

async function getUsersByDefaultShop(
  shopId: string,
): Promise<DefaultShopUser[]> {
  const { data } = await wrapperRequests(
    routesURL.settings.users.getUsersByDefaultShop(shopId),
    "GET",
  );
  return DefaultShopUsersResponse.parse(data).users;
}

const channelAdvisorFieldNames = new Set([
  "channelAdvisorDistributionCenterID",
  "channelAdvisorDistributionCenterCode",
]);

export function buildStoreUpdatePayload(
  data: TStoreSchema,
): UpdateShopInputDTO {
  const dataWithoutChannelAdvisor = Object.fromEntries(
    Object.entries(data).filter(([key]) => !channelAdvisorFieldNames.has(key)),
  );

  const dataWithValidValues = Object.fromEntries(
    Object.entries(dataWithoutChannelAdvisor).filter(
      ([key, value]) =>
        key === "zapietLocationIDs" ||
        key === "geolocationLat" ||
        key === "geolocationLon" ||
        value !== "",
    ),
  );

  return {
    ...dataWithValidValues,
    zapietLocationIDs:
      typeof dataWithValidValues?.zapietLocationIDs === "string"
        ? dataWithValidValues.zapietLocationIDs
            .split(",")
            .map((str) => str.trim())
            .filter(
              (str) =>
                str !== "" || dataWithValidValues.zapietLocationIDs === "",
            )
        : undefined,
  };
}

export function RegisterStore() {
  const { t, i18n } = useTranslation();
  const [initialValues, setInicialValues] = useState({} as UpdateShopInputDTO);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDrawerOpen, setDeleteDrawerOpen] = useState(false);
  const [addPrinterDrawerOpen, setAddPrinterDrawerOpen] = useState(false);
  const [addPrinterModalOpen, setAddPrinterModalOpen] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState<IPrinter | null>(null);
  const [defaultShopUsers, setDefaultShopUsers] = useState<DefaultShopUser[]>(
    [],
  );
  const [defaultShopUsersModalOpen, setDefaultShopUsersModalOpen] =
    useState(false);

  const theme = useTheme();
  const matches = useMediaQuery(`(max-width: ${theme.responsiveDesign.sm})`);

  const isEnglishLanguage = i18n.language.startsWith("en");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TStoreSchema>({
    resolver: zodResolver(StoreSchema),
  });

  const { editStore, fetchStore, selectedStore, setSelectedStore } =
    useContextSelector(StoresContext, (context) => {
      return context;
    });

  const location = useLocation();
  const navigate = useNavigate();
  const storeId = location.pathname.split("/").pop();
  const active = watch("active");
  const currentValues = watch();

  const openDefaultShopUsersModalIfAny = async (
    shopId: string,
  ): Promise<boolean> => {
    try {
      const users = await getUsersByDefaultShop(shopId);
      if (users.length === 0) return false;

      setDefaultShopUsers(users);
      setDefaultShopUsersModalOpen(true);
      setValue("active", true);
      return true;
    } catch {
      // The shop service still validates deactivation when this lookup fails.
      return false;
    }
  };

  useEffect(() => {
    setDeleteDrawerOpen(false);
    setAddPrinterDrawerOpen(false);
  }, [matches]);

  const handleRegisterStore = async (data: TStoreSchema) => {
    setIsLoading(true);

    const isDeactivation = selectedStore.active && data.active === false;
    const shopId = selectedStore.id || storeId;

    try {
      if (
        isDeactivation &&
        shopId &&
        (await openDefaultShopUsersModalIfAny(shopId))
      ) {
        return;
      }

      await editStore(buildStoreUpdatePayload(data), !!isDeactivation);

      navigate("/settings/stores");
    } catch (error) {
      if (isDeactivation) {
        const message =
          error instanceof AxiosError
            ? error.response?.data?.message
            : undefined;

        if (
          typeof message === "string" &&
          message.includes(defaultShopBlockMessage)
        ) {
          setValue("active", true);

          if (shopId && (await openDefaultShopUsersModalIfAny(shopId))) {
            return;
          }
        }

        toastify("error", message || t("storeEditError"), "top-center");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePrinter = async (id: string) => {
    setIsLoading(true);
    try {
      await wrapperRequests(
        routesURL.settings.stores.deleteStorePrinter(
          selectedStore.tenantName,
          selectedStore.code,
          id,
        ),
        "PUT",
      );

      setSelectedStore((prevState) => ({
        ...prevState,
        printers: prevState.printers?.filter((printer) => printer.id !== id),
      }));

      toastify("success", t("printerDeleted"), "top-center");
    } catch (error) {
      const errorMessage =
        error instanceof AxiosError
          ? error.response?.data?.message || t("printerDeletedError")
          : t("printerDeletedError");

      toastify("error", errorMessage, "top-center");
    } finally {
      setIsLoading(false);
      setDeleteDrawerOpen(false);
    }
  };

  const getStore = async (storeId: string) => {
    const response = await fetchStore(storeId);

    setInicialValues({
      active: response.active || false,
      displayName: response.displayName || "",
      geolocationLat: response.geolocationLat?.toString() || "",
      geolocationLon: response.geolocationLon?.toString() || "",
      shipstationTagIdShipping:
        response.shipStationProperties?.tagIdShopShipping || "",
      shipstationTagIdDelivery:
        response.shipStationProperties?.tagIdDelivery || "",
      shipstationTagIdStorePickup:
        response.shipStationProperties?.tagIdStorePickup || "",
      shipstationCompanyName: response.shipStationProperties?.companyName || "",
      zapietLocationIDs:
        joinArray(response.shipStationProperties?.zapietLocationIDs) || "",
    });

    reset({
      active: response.active || false,
      displayName: response.displayName,
      geolocationLat: response.geolocationLat?.toString(),
      geolocationLon: response.geolocationLon?.toString(),
      shipstationTagIdShipping:
        response.shipStationProperties?.tagIdShopShipping,
      shipstationTagIdDelivery: response.shipStationProperties?.tagIdDelivery,
      shipstationTagIdStorePickup:
        response.shipStationProperties?.tagIdStorePickup,
      shipstationCompanyName: response.shipStationProperties?.companyName || "",
      zapietLocationIDs:
        joinArray(response.shipStationProperties?.zapietLocationIDs) || "",
    });
  };

  useEffect(() => {
    if (storeId) {
      getStore(storeId);
    }

    return () => {
      setSelectedStore({} as IStore);
    };
  }, [storeId]);

  const handlePrinterSuccess = async () => {
    if (storeId) {
      await getStore(storeId);
    }

    closePrinterModal();
  };

  const closePrinterModal = () => {
    setAddPrinterModalOpen(false);
    setAddPrinterDrawerOpen(false);
    setSelectedPrinter(null);
  };

  const handleEditPrinter = (printer: IPrinter) => {
    setSelectedPrinter(printer);
    if (matches) {
      setAddPrinterDrawerOpen(true);
    } else {
      setAddPrinterModalOpen(true);
    }
  };

  const handleAddNewPrinter = () => {
    setSelectedPrinter(null);
    if (matches) {
      setAddPrinterDrawerOpen(true);
    } else {
      setAddPrinterModalOpen(true);
    }
  };

  useLeavePageConfirm(hasUnsavedChanges(initialValues, currentValues));

  const identifyPrinterFromRow = (element: HTMLElement): IPrinter | null => {
    const rowIndexAttr = element.getAttribute("data-row-index");

    if (rowIndexAttr && selectedStore?.printers) {
      const rowIndex = parseInt(rowIndexAttr);
      if (rowIndex >= 0 && rowIndex < selectedStore.printers.length) {
        return selectedStore.printers[rowIndex];
      }
    }

    return null;
  };

  const handleTableRowClick = (event: React.MouseEvent<HTMLElement>) => {
    if (matches) {
      const clickedRow = event.currentTarget.closest("tr");

      if (clickedRow) {
        const printer = identifyPrinterFromRow(clickedRow);

        if (printer) {
          setSelectedPrinter(printer);
          setAddPrinterDrawerOpen(true);
        }
      }
    }
  };

  const columns: ColumnDef<IPrinter>[] = [
    {
      accessorKey: "serialNumber",
      header: t("serialNumber"),
      size: 150,
      cell: (props) => <p>{props.getValue()}</p>,
    },
    {
      accessorKey: "tag",
      header: t("tag"),
      size: 100,
      cell: (props) => (
        <p title={props.getValue<string>()}>
          {ReduceString(props.getValue<string>(), 15)}
        </p>
      ),
    },
    {
      accessorKey: "size",
      size: 140,
      header: t("size"),
      cell: (props) => <p>{props.getValue()}</p>,
    },
    {
      accessorKey: "activeForOrderItem",
      size: 55,
      header: t("orderLabel"),
      cell: (props) => (
        <S.RowValueContainer isAcvite={props.row.original.activeForOrderItem}>
          {props.getValue() ? t("on") : t("off")}
        </S.RowValueContainer>
      ),
    },
    {
      accessorKey: "activeForProductPrice",
      size: 70,
      header: t("productPrice"),
      cell: (props) => (
        <S.RowValueContainer
          isAcvite={props.row.original.activeForProductPrice}
        >
          {props.getValue() ? t("on") : t("off")}
        </S.RowValueContainer>
      ),
    },
    {
      accessorKey: "activeForWarehouseLabel",
      size: 94,
      header: t("warehouseLabel"),
      cell: (props) => (
        <S.RowValueContainer
          isAcvite={props.row.original.activeForWarehouseLabel}
        >
          {props.getValue() ? t("on") : t("off")}
        </S.RowValueContainer>
      ),
    },
    {
      accessorKey: "activeForTransferCompleteLabel",
      size: 77,
      header: t("transferLabel"),
      cell: (props) => (
        <S.RowValueContainer
          isAcvite={props.row.original.activeForTransferCompleteLabel}
        >
          {props.getValue() ? t("on") : t("off")}
        </S.RowValueContainer>
      ),
    },
    {
      accessorKey: "activeForManualTransferCompleteLabel",
      size: 190,
      header: t("manualTransferLabel"),
      cell: (props) => (
        <S.RowValueContainer
          isAcvite={props.row.original.activeForManualTransferCompleteLabel}
        >
          {props.getValue() ? t("on") : t("off")}
        </S.RowValueContainer>
      ),
    },
    {
      accessorKey: "active",
      size: 70,
      header: t("status"),
      cell: (props) => (
        <S.RowValueContainer isAcvite={props.row.original.active}>
          {props.getValue() ? t("active") : t("inactive")}
        </S.RowValueContainer>
      ),
    },
    {
      accessorKey: "",
      size: 50,
      header: t("actions"),
      cell: (props) => (
        <S.IconContainer>
          <MdOutlineModeEditOutline
            size={16}
            title={t("edit")}
            onClick={(e) => {
              e.stopPropagation();
              handleEditPrinter(props.row.original);
            }}
          />

          {matches ? (
            <DeleteIcon
              title={t("deletePrinter")}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPrinter(props.row.original);
                setDeleteDrawerOpen(true);
              }}
            />
          ) : (
            <Modal
              customButtons
              modalContent={
                <DeleteModal
                  message={t("confirmDeletePrinter", {
                    tag: props.row.original.tag,
                  })}
                  onClick={() => handleDeletePrinter(props.row.original.id)}
                  isLoading={isLoading}
                />
              }
              title={t("deletePrinter")}
              width="21.25rem"
            >
              <DeleteIcon title={t("deletePrinter")} />
            </Modal>
          )}
        </S.IconContainer>
      ),
    },
  ];

  return (
    <S.RegisterStoreContainer>
      <PageHeader
        title={t("editStore")}
        customBreadcrumbString={selectedStore.displayName}
      />

      <S.RegisterStoreForm onSubmit={handleSubmit(handleRegisterStore)}>
        <S.FieldsContainer>
          <SwitchInput
            title={t("status")}
            label={active ? t("active") : t("disabled")}
            checked={!!active}
            disabled={isLoading}
            onChange={() => setValue("active", !active)}
          />
        </S.FieldsContainer>

        <h3>{t("storeInformation")}</h3>
        <S.FieldsContainer>
          <Input
            defaultValue={selectedStore.name}
            disabled
            hasAsterisk
            title={t("storeName")}
          />
          <Input
            title={t("displayName")}
            defaultValue={selectedStore.displayName}
            {...register("displayName")}
            errorMessage={errors.displayName?.message}
            hasAsterisk
          />
          <Input
            defaultValue={selectedStore.code}
            disabled
            title={t("storeId")}
            hasAsterisk
          />
        </S.FieldsContainer>
        <Input
          defaultValue={selectedStore?.address1 || ""}
          disabled
          title={t("address")}
          hasAsterisk
        />

        <S.FieldsContainer>
          <Input
            defaultValue={selectedStore.tenantName}
            disabled
            title={t("posAccountName")}
            hasAsterisk
          />
          <Input
            defaultValue={selectedStore?.tenant?.lightspeedAccountId || ""}
            disabled
            title={t("posAccountNumber")}
            hasAsterisk
          />
        </S.FieldsContainer>
        <S.Separator style={{ margin: matches ? "1.5rem 0" : "2.18rem 0" }} />

        <h3>{t("locationCoordinates")}</h3>
        <S.FieldsContainer>
          <Input
            step="any"
            defaultValue={selectedStore.geolocationLat}
            title={t("geolocationLatitude")}
            hasAsterisk
            type="number"
            {...register("geolocationLat")}
            errorMessage={errors.geolocationLat?.message}
          />
          <Input
            step="any"
            defaultValue={selectedStore.geolocationLon}
            title={t("geolocationLongitude")}
            hasAsterisk
            type="number"
            {...register("geolocationLon")}
            errorMessage={errors.geolocationLon?.message}
          />
        </S.FieldsContainer>
        <S.Separator />

        <h3>{t("shippingInformation")}</h3>
        <S.FieldsContainer>
          <Input
            title={t("shipStationTagIdShopShipping")}
            defaultValue={
              selectedStore.shipStationProperties?.tagIdShopShipping
            }
            {...register("shipstationTagIdShipping")}
            errorMessage={errors.shipstationTagIdShipping?.message}
            hasAsterisk
          />
          <Input
            title={t("shipStationTagIdDelivery")}
            defaultValue={selectedStore.shipStationProperties?.tagIdDelivery}
            {...register("shipstationTagIdDelivery")}
            errorMessage={errors.shipstationTagIdDelivery?.message}
            hasAsterisk
          />
          <Input
            title={t("shipStationTagIdStorePickup")}
            defaultValue={selectedStore.shipStationProperties?.tagIdStorePickup}
            {...register("shipstationTagIdStorePickup")}
            errorMessage={errors.shipstationTagIdStorePickup?.message}
            hasAsterisk
          />
        </S.FieldsContainer>

        <S.FieldsContainer>
          <Input
            title={t("shipStationCompanyName")}
            defaultValue={selectedStore.shipStationProperties?.companyName}
            {...register("shipstationCompanyName")}
            errorMessage={errors.shipstationCompanyName?.message}
            hasAsterisk
          />
        </S.FieldsContainer>

        <S.FieldsContainer>
          <Input
            title={t("zapietLocationIds")}
            defaultValue={joinArray(
              selectedStore.shipStationProperties?.zapietLocationIDs,
            )}
            {...register("zapietLocationIDs")}
            errorMessage={errors.zapietLocationIDs?.message}
            hasAsterisk
          />
          <Input title={t("zapietPickup")} disabled hasAsterisk />
          <Input title={t("zapietDelivery")} disabled hasAsterisk />
        </S.FieldsContainer>

        <S.ButtonAlignContainer>
          <Button
            title={t("submit")}
            isLoading={isLoading}
            disabled={isLoading}
          />
        </S.ButtonAlignContainer>

        <S.Separator />
        <h3>{t("warehouseInformation")}</h3>
        <S.FieldsContainer>
          <Input
            title={t("warehouseShopShopId")}
            defaultValue={selectedStore.warehouseShop?.shopID}
            disabled
            hasAsterisk
          />
          <Input
            title={t("warehouseShopTenantName")}
            defaultValue={selectedStore.warehouseShop?.tenantName}
            disabled
            hasAsterisk
          />
        </S.FieldsContainer>
      </S.RegisterStoreForm>

      <Modal
        title={t("cannotDeactivateStore")}
        isOpen={defaultShopUsersModalOpen}
        onOpenChange={setDefaultShopUsersModalOpen}
        customButtons
        width="min(36rem, calc(100vw - 2rem))"
        modalContent={
          <>
            <S.DefaultShopUsersDescription>
              {t("defaultShopUsersDescription", {
                store: selectedStore.displayName || selectedStore.name,
              })}
            </S.DefaultShopUsersDescription>
            <S.DefaultShopUsersList>
              {defaultShopUsers.map((user) => (
                <li key={user.id}>
                  <strong>{user.name}</strong>
                  <span>{user.email}</span>
                </li>
              ))}
            </S.DefaultShopUsersList>
            <S.DefaultShopUsersActions>
              <Button
                type="button"
                title={t("close")}
                variant="ghost"
                onClick={() => setDefaultShopUsersModalOpen(false)}
              />
            </S.DefaultShopUsersActions>
          </>
        }
      />
      <S.Separator style={{ margin: matches ? "1.5rem 0" : "2rem 0" }} />

      <S.PrinterTitleAlign
        style={{ margin: 0, marginBottom: matches ? 24 : 32 }}
      >
        <h3>{t("printerInformation")}</h3>
        {matches ? (
          <Button
            disabled={isLoading || !selectedStore.active}
            isLoading={isLoading}
            width={isEnglishLanguage ? "9.37rem" : "auto"}
            title={t("addNewPrinter")}
            onClick={handleAddNewPrinter}
          />
        ) : (
          <Modal
            title={t("printerInformation")}
            customButtons
            isOpen={addPrinterModalOpen}
            onOpenChange={(open) => {
              if (!open) {
                closePrinterModal();
              } else {
                setAddPrinterModalOpen(open);
              }
            }}
            modalContent={
              <PrintersModalContent
                storeData={selectedStore}
                printer={selectedPrinter ?? undefined}
                onSuccess={handlePrinterSuccess}
              />
            }
          >
            <Button
              disabled={isLoading || !selectedStore.active}
              isLoading={isLoading}
              width={isEnglishLanguage ? "9.37rem" : "auto"}
              title={t("addNewPrinter")}
              onClick={handleAddNewPrinter}
            />
          </Modal>
        )}
      </S.PrinterTitleAlign>

      {selectedStore?.printers && selectedStore?.printers[0] && (
        <Table
          columns={columns}
          columnToFilter="task"
          completeData={selectedStore}
          data={selectedStore?.printers}
          key={JSON.stringify(selectedStore?.printers)}
          modalOnClick={false}
          onClick={handleTableRowClick}
        />
      )}
      <S.Separator />

      <h3>{t("shopMetadata")}</h3>
      <S.FieldsContainer>
        <Input
          title={t("shopsId")}
          defaultValue={selectedStore.shipstationShopMetadata?._id}
          disabled
          hasAsterisk
        />
        <Input
          title={t("shipstationShopMetadataRegionName")}
          defaultValue={selectedStore.shipstationShopMetadata?.regionName}
          disabled
          hasAsterisk
        />
        <Input
          title={t("shipstationShopMetadataUserId")}
          defaultValue={selectedStore.shipstationShopMetadata?.userId}
          disabled
          hasAsterisk
        />
      </S.FieldsContainer>

      <S.FieldsContainer>
        <Input
          title={t("shipstationShopMetadataShops")}
          defaultValue={selectedStore.shipstationShopMetadata?.shops.join(", ")}
          disabled
          hasAsterisk
        />
      </S.FieldsContainer>

      {matches && deleteDrawerOpen && selectedPrinter && (
        <DrawerComponent
          open={deleteDrawerOpen}
          onOpenChange={setDeleteDrawerOpen}
          triggerContent={null}
          description=""
          title={t("deletePrinter")}
        >
          <S.DrawerContent>
            <p>{t("confirmDeletePrinter", { tag: selectedPrinter.tag })}</p>

            <Button
              title={t("confirm")}
              variant="destructive"
              width="100%"
              onClick={() => handleDeletePrinter(selectedPrinter.id)}
              isLoading={isLoading}
              disabled={isLoading}
            />
            <Button
              title={t("cancel")}
              variant="ghost"
              width="100%"
              onClick={() => setDeleteDrawerOpen(false)}
            />
          </S.DrawerContent>
        </DrawerComponent>
      )}

      {matches && addPrinterDrawerOpen && (
        <DrawerComponent
          open={addPrinterDrawerOpen}
          onOpenChange={(open) => {
            if (!open) {
              closePrinterModal();
            } else {
              setAddPrinterDrawerOpen(open);
            }
          }}
          triggerContent={null}
          description=""
          title={selectedPrinter?.id ? t("editPrinter") : t("addNewPrinter")}
          showCloseButton
          showSeparator
        >
          <PrintersModalContent
            storeData={selectedStore}
            printer={selectedPrinter ?? undefined}
            onSuccess={handlePrinterSuccess}
          />
        </DrawerComponent>
      )}
    </S.RegisterStoreContainer>
  );
}
